/**
 * Alert flow over the period: new alerts and reviewed cycles.
 *
 * Two INDEPENDENT lines on ONE axis — same unit (counts), so they compare
 * directly. Deliberately not stacked, not a ratio, and neither clamped to the
 * other: reviewed cycles can legitimately exceed newly received alerts when
 * operators clear carryover from an earlier period, and stacking would present
 * that as a total while a ratio would present it as an accuracy figure. It is
 * neither.
 *
 * Colors come from the app's categorical chart tokens in fixed order, so a
 * series keeps its hue no matter how many are shown. Both series carry a legend
 * AND a direct end-label: the green step sits under 3:1 against the light
 * surface, which obliges a non-color identifier.
 */
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatSiteTime } from "@/alerts/utils/format";
import type { AlertAnalyticsShift, AlertAnalyticsTrendPoint } from "@/alerts/types/alerts-analytics";

const SERIES = [
  { key: "alerts_received", labelKey: "flowChart.series.received", color: "hsl(var(--chart-1))" },
  { key: "alerts_reviewed", labelKey: "flowChart.series.reviewed", color: "hsl(var(--chart-2))" },
] as const;

const ChartTooltip = ({
  active,
  payload,
  timezone,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; payload: AlertAnalyticsTrendPoint }>;
  timezone: string | null;
}) => {
  const { t } = useTranslation("alerts");
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-background px-2.5 py-2 text-xs shadow-md">
      <p className="mb-1 font-medium">{formatSiteTime(point.start_time, timezone)}</p>
      {SERIES.map((series) => (
        <p key={series.key} className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: series.color }}
          />
          {/* Text stays in ink tokens; the swatch carries the identity. */}
          <span className="text-muted-foreground">{t(series.labelKey)}</span>
          <span className="ml-auto font-medium tabular-nums">
            {point[series.key as keyof AlertAnalyticsTrendPoint] as number}
          </span>
        </p>
      ))}
    </div>
  );
};

export const AlertFlowChart = ({
  trend,
  shifts,
  timezone,
}: {
  trend: AlertAnalyticsTrendPoint[];
  /** Whole-day view only: draws shift boundaries as reference lines. */
  shifts?: AlertAnalyticsShift[] | null;
  timezone: string | null;
}) => {
  const { t } = useTranslation("alerts");

  if (trend.length === 0) {
    return (
      <div className="flex h-56 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
        {t("flowChart.empty")}
      </div>
    );
  }

  const last = trend[trend.length - 1];

  return (
    <div>
      {/* Legend: always present for two series, and never the only identifier. */}
      <div className="mb-2 flex flex-wrap items-center gap-3">
        {SERIES.map((series) => (
          <span key={series.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span
              aria-hidden
              className="inline-block h-0.5 w-4 rounded-full"
              style={{ backgroundColor: series.color }}
            />
            {t(series.labelKey)}
            <span className="font-medium tabular-nums text-foreground">
              {last[series.key as keyof AlertAnalyticsTrendPoint] as number}
            </span>
          </span>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={trend} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          {/* Recessive grid + axes — the data carries the emphasis. */}
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
          <XAxis
            dataKey="start_time"
            tickFormatter={(value: string) => formatSiteTime(value, timezone).split(", ")[1] ?? ""}
            tick={{ fontSize: 11 }}
            stroke="hsl(var(--border))"
            tickLine={false}
          />
          {/* ONE axis. Both series are counts, so a second scale would make
              their crossing points meaningless. */}
          <YAxis
            allowDecimals={false}
            width={36}
            tick={{ fontSize: 11 }}
            stroke="hsl(var(--border))"
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
            content={<ChartTooltip timezone={timezone} />}
          />

          {/* Shift boundaries, so a whole-day reading can be related back to
              who was on shift. Backend-provided — never derived here. */}
          {shifts?.map((shift) => (
            <ReferenceLine
              key={shift.shift_key}
              x={shift.start_time}
              stroke="hsl(var(--border))"
              strokeDasharray="2 3"
            />
          ))}

          {SERIES.map((series) => (
            <Line
              key={series.key}
              type="monotone"
              dataKey={series.key}
              stroke={series.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
